import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Home, Plus, Table2, Grid3X3, Columns, Calendar, Image, Menu, FileText, Zap, Settings, MoreHorizontal, Trash2, Lock, Brain } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GridView } from "@/components/grid/GridView";
import { KanbanView } from "@/components/grid/KanbanView";
import { CalendarView } from "@/components/grid/CalendarView";
import { GalleryView } from "@/components/grid/GalleryView";
import { SmartTableCreator } from "@/components/base/SmartTableCreator";
import { FormsList } from "@/components/forms/FormsList";
import { AutomationsList } from "@/components/automations/AutomationsList";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FeatureGate } from "@/components/FeatureGate";
import { useSubscription } from "@/hooks/useSubscription";
import { ProactiveAgent } from "@/components/agent/ProactiveAgent";
import { MobileGridView } from "@/components/mobile/MobileGridView";
import { useIsMobile } from "@/hooks/use-mobile";
import logo from "@/assets/logo.png";

const Base = () => {
  const { baseId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [base, setBase] = useState<any>(null);
  const [tables, setTables] = useState<any[]>([]);
  const [selectedTable, setSelectedTable] = useState<any>(null);
  const [activeView, setActiveView] = useState("grid");
  const [activeSection, setActiveSection] = useState("tables");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [baseName, setBaseName] = useState("");
  const [baseIcon, setBaseIcon] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Get workspace ID for tier gating
  const workspaceId = base?.workspace_id;
  const { hasFeature, gateFeature } = useSubscription(workspaceId);
  const isMobile = useIsMobile();
  const [showAgentPanel, setShowAgentPanel] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && baseId) {
      fetchBase();
      fetchTables();
    }
  }, [user, baseId]);

  const fetchBase = async () => {
    try {
      const { data, error } = await supabase
        .from("bases")
        .select("*, workspaces(id, name)")
        .eq("id", baseId)
        .single();

      if (error) throw error;
      setBase(data);
      setBaseName(data.name);
      setBaseIcon(data.icon || "📊");
    } catch (error: any) {
      toast.error("Failed to load base");
      navigate("/dashboard");
    }
  };

  const fetchTables = async () => {
    try {
      const { data, error } = await supabase
        .from("tables")
        .select("*")
        .eq("base_id", baseId)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setTables(data || []);
      if (data && data.length > 0) {
        setSelectedTable(data[0]);
      }
    } catch (error: any) {
      toast.error("Failed to load tables");
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = (table: any) => {
    setSelectedTable(table);
    setSidebarOpen(false);
  };

  const saveBaseSettings = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("bases")
        .update({ name: baseName, icon: baseIcon })
        .eq("id", baseId);

      if (error) throw error;
      setBase({ ...base, name: baseName, icon: baseIcon });
      toast.success("Base updated!");
      setSettingsOpen(false);
    } catch (error) {
      toast.error("Failed to update base");
    } finally {
      setSaving(false);
    }
  };

  const deleteBase = async () => {
    try {
      const { error } = await supabase
        .from("bases")
        .delete()
        .eq("id", baseId);

      if (error) throw error;
      toast.success("Base deleted");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to delete base");
    }
  };

  if (authLoading || loading) {
    return <LoadingSpinner fullScreen text="Loading base..." />;
  }

  const TableSidebar = ({ className }: { className?: string }) => (
    <div className={cn("flex flex-col h-full", className)}>
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-xs uppercase text-muted-foreground tracking-wide">Tables</h3>
          <SmartTableCreator baseId={baseId!} onTableCreated={fetchTables} />
        </div>
      </div>
      <div className="space-y-0.5 flex-1 overflow-y-auto">
        {tables.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 px-2">No tables yet</p>
        ) : (
          tables.map((table) => (
            <button
              key={table.id}
              onClick={() => handleTableSelect(table)}
              className={cn(
                "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
                selectedTable?.id === table.id
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              )}
            >
              <div className="flex items-center gap-2">
                <Table2 className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{table.name}</span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Compact Header */}
      <nav className="border-b border-border bg-card flex-shrink-0 sticky top-0 z-40">
        <div className="px-4">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0"
                onClick={() => navigate("/dashboard")}
              >
                <Home className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-lg">{base?.icon}</span>
                <span className="font-semibold text-sm truncate max-w-[120px] md:max-w-none">{base?.name}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              {/* Agent Intelligence Toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowAgentPanel(!showAgentPanel)}
              >
                <Brain className="h-4 w-4" />
              </Button>
              
              {/* Mobile Table Selector */}
              <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="md:hidden h-8">
                    <Menu className="h-4 w-4 mr-1" />
                    <span className="text-xs">Tables</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-4">
                  <TableSidebar />
                </SheetContent>
              </Sheet>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Base Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setDeleteConfirmOpen(true)} className="text-destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Base
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Section Tabs */}
      <div className="border-b border-border bg-card/50">
        <Tabs value={activeSection} onValueChange={setActiveSection} className="px-4">
          <TabsList className="h-10 bg-transparent p-0 gap-4">
            <TabsTrigger 
              value="tables" 
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-2"
            >
              <Table2 className="h-4 w-4 mr-1.5" />
              <span className="text-sm">Data</span>
            </TabsTrigger>
            <TabsTrigger 
              value="forms" 
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-2"
            >
              <FileText className="h-4 w-4 mr-1.5" />
              <span className="text-sm">Forms</span>
            </TabsTrigger>
            <TabsTrigger 
              value="automations" 
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-2"
            >
              <Zap className="h-4 w-4 mr-1.5" />
              <span className="text-sm">Automations</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {activeSection === "tables" && (
          <>
            {/* Desktop Sidebar */}
            <aside className="hidden md:block w-52 border-r border-border bg-card/50 p-3 flex-shrink-0 overflow-y-auto">
              <TableSidebar />
            </aside>

            {/* Table Content */}
            <main className="flex-1 overflow-hidden flex flex-col min-w-0">
              {!selectedTable ? (
                <div className="flex-1 flex items-center justify-center p-4">
                  <div className="text-center">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Table2 className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">No table selected</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create a table to start managing data
                    </p>
                    <SmartTableCreator baseId={baseId!} onTableCreated={fetchTables}>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Table
                      </Button>
                    </SmartTableCreator>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* View Tabs - Gate Advanced Views */}
                  <div className="border-b border-border bg-muted/30 px-3 py-2 flex-shrink-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate md:hidden">
                        {selectedTable.name}
                      </span>
                      <Tabs value={activeView} onValueChange={(view) => {
                        // Gate advanced views
                        if (view !== "grid" && !hasFeature("has_advanced_views")) {
                          gateFeature("has_advanced_views");
                          return;
                        }
                        setActiveView(view);
                      }} className="w-full md:w-auto">
                        <TabsList className="h-8 grid grid-cols-4 md:flex w-full md:w-auto">
                          <TabsTrigger value="grid" className="text-xs h-7 px-2">
                            <Grid3X3 className="h-3.5 w-3.5 md:mr-1.5" />
                            <span className="hidden md:inline">Grid</span>
                          </TabsTrigger>
                          <TabsTrigger value="kanban" className="text-xs h-7 px-2">
                            <Columns className="h-3.5 w-3.5 md:mr-1.5" />
                            <span className="hidden md:inline">Kanban</span>
                            {!hasFeature("has_advanced_views") && <Lock className="h-3 w-3 ml-1 opacity-50" />}
                          </TabsTrigger>
                          <TabsTrigger value="calendar" className="text-xs h-7 px-2">
                            <Calendar className="h-3.5 w-3.5 md:mr-1.5" />
                            <span className="hidden md:inline">Calendar</span>
                            {!hasFeature("has_advanced_views") && <Lock className="h-3 w-3 ml-1 opacity-50" />}
                          </TabsTrigger>
                          <TabsTrigger value="gallery" className="text-xs h-7 px-2">
                            <Image className="h-3.5 w-3.5 md:mr-1.5" />
                            <span className="hidden md:inline">Gallery</span>
                            {!hasFeature("has_advanced_views") && <Lock className="h-3 w-3 ml-1 opacity-50" />}
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-auto">
                    {activeView === "grid" && (
                      isMobile ? (
                        <MobileGridView table={selectedTable} workspaceId={workspaceId} />
                      ) : (
                        <GridView table={selectedTable} />
                      )
                    )}
                    {activeView === "kanban" && <KanbanView table={selectedTable} />}
                    {activeView === "calendar" && <CalendarView table={selectedTable} />}
                    {activeView === "gallery" && <GalleryView table={selectedTable} />}
                  </div>
                </div>
              )}
            </main>
          </>
        )}

        {activeSection === "forms" && (
          <div className="flex-1 overflow-auto p-4">
            {workspaceId && hasFeature("has_forms") ? (
              <FormsList baseId={baseId} />
            ) : (
              <FeatureGate feature="has_forms" workspaceId={workspaceId || ""}>
                <FormsList baseId={baseId} />
              </FeatureGate>
            )}
          </div>
        )}

        {activeSection === "automations" && (
          <div className="flex-1 overflow-auto p-4">
            <AutomationsList workspaceId={base?.workspace_id} baseId={baseId} />
          </div>
        )}
      </div>

      {/* Proactive Agent Panel */}
      <Sheet open={showAgentPanel} onOpenChange={setShowAgentPanel}>
        <SheetContent side={isMobile ? "bottom" : "right"} className={isMobile ? "h-[70vh]" : "w-96"}>
          <ProactiveAgent workspaceId={workspaceId} tableId={selectedTable?.id} />
        </SheetContent>
      </Sheet>

      {/* Base Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Base Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Input
                  value={baseIcon}
                  onChange={(e) => setBaseIcon(e.target.value)}
                  maxLength={2}
                  className="w-16 text-center text-xl"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label>Name</Label>
                <Input
                  value={baseName}
                  onChange={(e) => setBaseName(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveBaseSettings} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Base?</DialogTitle>
            <DialogDescription>
              This will permanently delete "{base?.name}" and all its tables, records, and automations. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteBase}>
              Delete Base
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileBottomNav />
    </div>
  );
};

export default Base;
